import { LngLat } from "mapbox-gl";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLeadStore, usePopupStore } from "../..";
import { useLeadBoeGenerator } from "../../hooks/boeGenerator/useLeadBoeGenerator";
import useClosable from "../../hooks/useClosable";
import useProperty from "../../hooks/useProperty";
import useRecentlyViewed from "../../hooks/useRecentlyViewed";
import DetailsType from "../../interfaces/DetailsType";
import ILead from "../../interfaces/ILead";
import IProperty from "../../interfaces/IProperty";
import { RecentItemType } from "../../interfaces/IRecentItem";
import { ClosableOrder } from "../../store/ClosableStore";
import ButtonWithTooltip from "../ButtonWithTooltip";
import commonStyles from "../Common.module.css";
import detailsStyles from "../Details.module.css";
import DetailsPanelWithFlyTo from "../DetailsPanelWithFlyTo";
import { LeadFavoriteCheckbox } from "../favorites/FavoriteCheckbox";
import CompanyIcon from "../icons/CompanyIcon";
import DealIcon from "../icons/DealIcon";
import DetailsIcon from "../icons/DetailsIcon";
import EditIcon from "../icons/EditIcon";
import GoogleMapsIcon from "../icons/GoogleMapsIcon";
import LeadIcon from "../icons/LeadIcon";
import LeaseCompIcon from "../icons/LeaseCompIcon";
import LocationIcon from "../icons/LocationIcon";
import PropertyIcon from "../icons/PropertyIcon";
import SaleCompIcon from "../icons/SaleCompIcon";
import TableConvertIcon from "../icons/TableConvertIcon";
import TenantInMarketIcon from "../icons/TenantInMarketIcon";
import IconWithTooltip from "../IconWithTooltip";
import SegmentedButtons from "../SegmentedButtons";
import OnMapSpinner from "../spinners/OnMapSpinner";
import Spinner from "../spinners/Spinner";
import LeadDetailsCompaniesTab from "./LeadDetailsCompaniesTab";
import LeadDetailsDealsTab from "./LeadDetailsDealsTab";
import LeadDetailsDetailsTab from "./LeadDetailsDetailsTab";
import LeadDetailsLeaseCompsTab from "./LeadDetailsLeaseCompsTab";
import LeadDetailsPropertyTab from "./LeadDetailsPropertyTab";
import LeadDetailsRecordedSalesTab from "./LeadDetailsRecordedSalesTab";
import LeadDetailsSaleCompsTab from "./LeadDetailsSaleCompsTab";
import LeadDetailsTenantsInMarketTab from "./LeadDetailsTenantsInMarketTab";

function LeadDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const leadStore = useLeadStore();
    const popupStore = usePopupStore();
    const lead = useMemo(() => computed(() => leadStore.leads.find(l => l.id.toString() === id)), [id]).get();
    const [leadTab, setLeadTab] = useState(LeadTab.Details);
    const lngLat = useMemo(() => lead && new LngLat(lead.centerPoint.longitude, lead.centerPoint.latitude), [lead]);
    const { property, fetchProperty } = useProperty(lngLat);
    const [companyCount, setCompanyCount] = useState(0);
    const [leaseCompCount, setLeaseCompCount] = useState(0);
    const [dealCount, setDealCount] = useState(0);
    const [saleCompCount, setSaleCompCount] = useState(0);
    const [recordedSaleCount, setRecordedSaleCount] = useState(0);
    const [tenantInMarketCount, setTenantInMarketCount] = useState(0);
    const segmentedButtons = useMemo(() => [
        {
            value: LeadTab.Details,
            label: (
                <IconWithTooltip title="Details">
                    <DetailsIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: LeadTab.PropertyInfo,
            label: (
                <IconWithTooltip title="Property Info">
                    <PropertyIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: LeadTab.Companies,
            label: (
                <IconWithTooltip title="Companies" count={companyCount}>
                    <CompanyIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: LeadTab.LeaseComps,
            label: (
                <IconWithTooltip title="Lease Comps" count={leaseCompCount}>
                    <LeaseCompIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: LeadTab.Deals,
            label: (
                <IconWithTooltip title="Deals" count={dealCount}>
                    <DealIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: LeadTab.SaleComps,
            label: (
                <IconWithTooltip title="Sale Comps" count={saleCompCount}>
                    <SaleCompIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: LeadTab.RecordedSales,
            label: (
                <IconWithTooltip title="Recorded Sales" count={recordedSaleCount}>
                    <LocationIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: LeadTab.TenantsInMarket,
            label: (
                <IconWithTooltip title="Tenants in Market" count={tenantInMarketCount}>
                    <TenantInMarketIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
    ], [companyCount, leaseCompCount, dealCount, saleCompCount, recordedSaleCount, tenantInMarketCount]);
    useRecentlyViewed(Number(id), RecentItemType.Lead);

    const close = () => navigate("/");
    useClosable(ClosableOrder.Medium, close);

    useEffect(() => {
        if (lead) {
            fetchProperty();
            popupStore.openLeadPopup(lead.id);

            if (!leadStore.leadIdsAttachmentLists[lead.id])
                leadStore.fetchLeadAttachments(lead.id);
        }

        return () => popupStore.close();
    }, [lead]);

    if (leadStore.isLeadsLoading.fetching)
        return <OnMapSpinner />;

    if (!lead)
        return null;

    return (
        <DetailsPanelWithFlyTo type={DetailsType.LeadDetails} coordinates={lead.centerPoint} onClose={close} controls={[
            <EditIcon key="edit" onClick={() => navigate(`/leads/${id}/edit`)} />,
            <LeadFavoriteCheckbox key="favorite" id={Number(id)} className={commonStyles.clickableIcon} />,
        ]}>
            <div className={detailsStyles.header}>
                <div className={detailsStyles.headerGroup}>
                    <LeadIcon color="#25479A" />
                    <div className={detailsStyles.title}>
                        <span className={commonStyles.link} onClick={() => popupStore.openLeadPopup(lead.id)}>{lead.address}</span>
                    </div>
                </div>
                <div className={[detailsStyles.headerGroup, commonStyles.hideInLandscape].join(" ")}>
                    <ButtonWithTooltip title="Google Maps" className={commonStyles.navigationButton} onClick={() => window.open(`https://www.google.com/maps/place/${lead.centerPoint.latitude},${lead.centerPoint.longitude}`)}>
                        <GoogleMapsIcon />
                    </ButtonWithTooltip>
                    <BoeDownloadButton lead={lead} property={property} />
                </div>
            </div>
            <SegmentedButtons
                value={leadTab}
                onChange={setLeadTab}
                options={segmentedButtons}
                className={detailsStyles.segmentedButtons}
            />
            <div className={detailsStyles.body}>
                {leadTab === LeadTab.Details && <LeadDetailsDetailsTab />}
                {leadTab === LeadTab.PropertyInfo && <LeadDetailsPropertyTab />}
                <LeadDetailsCompaniesTab visible={leadTab === LeadTab.Companies} setEntityCount={setCompanyCount} />
                <LeadDetailsLeaseCompsTab visible={leadTab === LeadTab.LeaseComps} setEntityCount={setLeaseCompCount} />
                <LeadDetailsDealsTab visible={leadTab === LeadTab.Deals} setEntityCount={setDealCount} />
                <LeadDetailsSaleCompsTab visible={leadTab === LeadTab.SaleComps} setEntityCount={setSaleCompCount} />
                <LeadDetailsRecordedSalesTab visible={leadTab === LeadTab.RecordedSales} setEntityCount={setRecordedSaleCount} />
                <LeadDetailsTenantsInMarketTab visible={leadTab === LeadTab.TenantsInMarket} setEntityCount={setTenantInMarketCount} />
            </div>
        </DetailsPanelWithFlyTo>
    );
}

export default observer(LeadDetails);

enum LeadTab {
    Details,
    PropertyInfo,
    Companies,
    LeaseComps,
    Deals,
    SaleComps,
    RecordedSales,
    TenantsInMarket,
}

const BoeDownloadButton = observer(function BoeDownloadButton({ lead, property }: IBoeDownloadButtonProps) {
    const { isGenerating, generateBoe } = useLeadBoeGenerator(lead, property)
    const isDisabled = isGenerating || !property;

    return (
        <ButtonWithTooltip title="Download BOE" className={commonStyles.navigationButton} disabled={isDisabled} onClick={() => generateBoe()}>
            {isGenerating ? <Spinner /> : <TableConvertIcon color={isDisabled ? "#707070" : "#25479A"} />}
        </ButtonWithTooltip>
    );
})

interface IBoeDownloadButtonProps {
    lead: ILead;
    property: IProperty | undefined;
}
